// react core
import { useContext, useState } from "react";

// date handling
import dayjs from "dayjs";

// material design
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// mui x-charts
import { BarChart } from "@mui/x-charts/BarChart";

// entzy resources
import configEntzy from "components/config/ConfigEntzy";
import { CounterTag } from "components/utils/common/CommonTags";

// fonts icons and colors
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle as iconCircle } from "@fortawesome/pro-solid-svg-icons";
import { faRectangle as iconRectangle } from "@fortawesome/pro-solid-svg-icons";
import { faClock as iconNotice } from "@fortawesome/pro-duotone-svg-icons";
import { faUsersLine as iconPeopleMin } from "@fortawesome/pro-duotone-svg-icons";
import { faUsersViewfinder as iconPeopleMax } from "@fortawesome/pro-duotone-svg-icons";
import { faHorizontalRule as iconLine } from "@fortawesome/pro-thin-svg-icons";
import { faChevronUp as iconUp } from "@fortawesome/pro-thin-svg-icons";
import { faChevronDown as iconDown } from "@fortawesome/pro-thin-svg-icons";

// entzy context and services
import { MainContext } from "components/main/MainContext";
import { EventContext } from "components/event/EventContext";
import { RoomDatesChartItemDetail } from "./RoomDatesChart";

function RoomLaunchpadUpdates(props) {
  const mainContext = useContext(MainContext);
  const eventContext = useContext(EventContext);

  const source = props.source;
  const mini = props.mini;
  const handleTabs = props.handleTabs || (() => {});

  const initDateRange = mini ? 10 : 20;
  const barHeight = 40;
  const disabledValue1 = 0.00001;
  const disabledValue2 = 0.00002;
  const runnerFlag1 = 99.99999;
  const runnerFlag2 = 99.99998;

  const [dateRange] = useState(initDateRange);
  const [datasetStartDate, setDatasetStartDate] = useState(dayjs());
  const [datasetEndDate, setDatasetEndDate] = useState(
    dayjs().add(dateRange, "days"),
  );

  const handleDateScroll = (direction) => {
    if (direction === "back") {
      setDatasetStartDate(datasetStartDate.subtract(dateRange, "days"));
      setDatasetEndDate(datasetEndDate.subtract(dateRange, "days"));
    } else {
      setDatasetStartDate(datasetStartDate.add(dateRange, "days"));
      setDatasetEndDate(datasetEndDate.add(dateRange, "days"));
    }
  };

  const handleDateSelect = (date) => {
    mainContext.updateViewerTrail({
      eventDateSelect:
        mainContext.state.viewer.trail.eventDateSelect?.id === date?.id
          ? null
          : date,
    });
  };

  // create datasets
  const tickingDataset = [];
  const launchedDataset = [];
  let currentDate;

  const getDateFlags = (date, currentDate) => {
    const tickingCount = date?.progress?.count || 0;
    const tickingTotal = date?.progress?.trigger?.total || 0;
    const isInPast = currentDate.isBefore(dayjs());
    const isInNoticePeriod =
      currentDate.isAfter(dayjs()) &&
      currentDate.isBefore(
        dayjs().add(eventContext.state.event.calendar.limits.notice, "days"),
      );
    const isLaunched = date?.ticking?.TickStatus === "triggered";
    // const isLaunchedAboveTrigger =
    //   tickingTotal >= 100 && date?.ticking?.TickStatus === "triggered";
    const isExpired = isInPast || isInNoticePeriod || date?.isExpired;
    const isCancelled =
      date?.isCancelled || date?.ticking?.TickStatus === "cancelled";
    const leadTicket = {
      name: (date?.progress?.tickets?.leading?.name || "").toUpperCase(),
      // limited name to fit bars
      shortName:
        (date?.progress?.tickets?.leading?.name || "")
          .toUpperCase()
          .substring(0, 8) + (isLaunched ? "" : " 🤔"),
    };
    return {
      tickingCount,
      tickingTotal,
      leadTicket,
      isInPast,
      isLaunched,
      isExpired,
      isCancelled,
      isInNoticePeriod,
    };
  };

  currentDate = dayjs(datasetStartDate);
  while (currentDate.isBefore(datasetEndDate)) {
    let id, date, baseDate, dateFlags, displayDate, displayShort, progressTotal;
    id = currentDate.format("YYYY-MM-DD");
    displayDate = currentDate.format("ddd DD MMM YY");
    displayShort = currentDate.format("dd DD MMM");
    baseDate = {
      id: currentDate.format("YYYY-MM-DD"),
      dow: currentDate.format("ddd"),
      month: currentDate.format("MMM"),
      day: currentDate.format("DD"),
      year: currentDate.format("YYYY"),
    };

    // check for active dates
    date = eventContext.state.event.calendar.dates.combined.active.find(
      (date) => date.id === id,
    );

    // check for historical launched dates
    if (
      !date &&
      eventContext.state.event.calendar.dates.launched.history.keys.includes(id)
    ) {
      date = {
        ...baseDate,
        id,
        progress: {
          trigger: {
            total: 100,
          },
        },
      };
    }

    // default any dates with no activity
    if (!date) {
      date = {
        ...baseDate,
      };
    }

    // set date flags
    dateFlags = getDateFlags(date, currentDate);

    // set ticking totals and add to dataset
    progressTotal = dateFlags.isLaunched
      ? 100
      : dateFlags.isInNoticePeriod
        ? disabledValue2
        : dateFlags.isExpired
          ? disabledValue1
          : date?.progress?.trigger?.total || 0;
    tickingDataset.push({
      id,
      date,
      displayDate,
      displayShort,
      progressTotal,
      targetTotal: 100 - progressTotal,
      ...dateFlags,
    });

    // set launched totals and add to dataset
    progressTotal = dateFlags.isCancelled
      ? disabledValue1
      : dateFlags.isInNoticePeriod && !dateFlags.isLaunched
        ? disabledValue2
        : dateFlags.isExpired && !dateFlags.isLaunched
          ? disabledValue1
          : !dateFlags.isLaunched && date.isRunnerOffer
            ? runnerFlag1
            : !dateFlags.isLaunched && date.isRunnerLaunchable
              ? runnerFlag2
              : (dateFlags.isLaunched && date?.progress?.capacity?.total) || 0;
    launchedDataset.push({
      id,
      date,
      displayDate,
      displayShort,
      progressTotal,
      targetTotal: 100 - progressTotal,
      ...dateFlags,
    });

    currentDate = currentDate.add(1, "day");
  }

  const barNames = (dataset) => {
    return dataset.map((date) => date.displayDate);
  };
  const barValues = (dataset) => {
    return dataset.map((date) => date.progressTotal);
  };
  const barColors = (datasetValues) => {
    return datasetValues.map((val) => {
      if (val === disabledValue1) {
        return "black";
      } else if (val === disabledValue2) {
        return "rgba(255, 153, 0, 0.1)";
      } else if (val === runnerFlag1) {
        return "rgba(255, 140, 0, 0.5)";
      } else if (val === runnerFlag2) {
        return "rgba(255, 140, 0, 0.2)";
      } else {
        return null;
      }
    });
  };

  const DateNavBar = () => {
    return (
      <Box className="box-default">
        <Box
          className="box-default half-width bg-white-t25 shadow-default action-pointer"
          onClick={() => handleDateScroll("back")}
          sx={{
            p: configEntzy.APP_SPACING_SM,
          }}
        >
          <Typography variant="h3">
            <FontAwesomeIcon icon={iconUp} color="white" fixedWidth />
          </Typography>
        </Box>
        <Box
          className="box-default half-width bg-white-t25 shadow-default action-pointer"
          onClick={() => handleDateScroll("forward")}
          sx={{
            p: configEntzy.APP_SPACING_SM,
          }}
        >
          <Typography variant="h3">
            <FontAwesomeIcon icon={iconDown} color="white" fixedWidth />
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box className="box-default">
      <Box
        className="box-default"
        sx={{
          p: configEntzy.APP_SPACING_MD,
        }}
      >
        <Box
          className="box-default"
          sx={{
            mb: configEntzy.APP_SPACING_XL,
          }}
          hidden={!["main"].includes(source)}
        >
          <Box
            className="box-inline bg-orange action-pointer border-glow"
            sx={{
              p: configEntzy.APP_SPACING_MD,
              borderRadius: configEntzy.BORDER_SIZE_XL,
            }}
            // onClick={() => handleTabs(null, 3)}
            onClick={() => eventContext.focusModule("dates")}
          >
            <Typography variant="subtitle2" color="white" fontWeight={900}>
              &nbsp;Place Your Date Offers&nbsp;
            </Typography>
          </Box>
        </Box>
        {/* ------------- CRITERIA COUNTERS HEADER ------------- */}
        <Box
          className="box-default action-pointer"
          onClick={() => handleTabs(null, 6)}
        >
          <Box className="box-default quarter-width">
            <Box className="box-default relative pop-up">
              <CounterTag
                count={eventContext.state.event.calendar.limits.notice + "d"}
                size="sm"
                border={true}
              />
            </Box>
            <Box
              className="box-default"
              sx={{ mt: configEntzy.APP_SPACING_NSM }}
            >
              <Typography variant="h1">
                <span className="fa-layers fa-fw">
                  <FontAwesomeIcon
                    icon={iconLine}
                    transform="rotate-90 down-6 grow-24"
                  />
                  <FontAwesomeIcon
                    icon={iconCircle}
                    transform="up-1"
                    color="#333"
                  />
                  <FontAwesomeIcon
                    icon={iconNotice}
                    transform="shrink-4 up-1"
                    color="orange"
                  />
                </span>
              </Typography>
            </Box>
            <Box className="box-default relative pop-up">
              <Box
                className="box-inline bg-white"
                sx={{
                  borderRadius: configEntzy.BORDER_SIZE_MD,
                  pl: configEntzy.APP_SPACING_SM,
                  pr: configEntzy.APP_SPACING_SM,
                }}
              >
                <Typography variant="subtitle2" color="black">
                  Notice
                </Typography>
              </Box>
            </Box>
            <Box className="box-default">
              <Typography variant="body1">&nbsp;</Typography>
            </Box>
          </Box>
          <Box className="box-default half-width">
            <Box className="box-default relative pop-up">
              <CounterTag
                count={eventContext.state.event.calendar.triggers.default}
                size="md"
                border={true}
              />
            </Box>
            <Box
              className="box-default"
              sx={{ mt: configEntzy.APP_SPACING_NSM }}
            >
              <Typography variant="h1">
                <span className="fa-layers fa-fw">
                  <FontAwesomeIcon
                    icon={iconLine}
                    transform="rotate-90 down-6 grow-24"
                  />
                  <FontAwesomeIcon
                    icon={iconRectangle}
                    transform="down-3"
                    color="darkorange"
                  />
                  <FontAwesomeIcon
                    icon={iconPeopleMin}
                    transform="shrink-7 down-3"
                    color="black"
                  />
                </span>
              </Typography>
            </Box>
            <Box
              className="box-default relative pop-up"
              sx={{
                pt: configEntzy.APP_SPACING_SM,
              }}
            >
              <Box
                className="box-inline bg-white"
                sx={{
                  borderRadius: configEntzy.BORDER_SIZE_MD,
                  pl: configEntzy.APP_SPACING_SM,
                  pr: configEntzy.APP_SPACING_SM,
                }}
              >
                <Typography variant="subtitle2" color="black">
                  Launch
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box className="box-default quarter-width">
            <Box className="box-default relative pop-up">
              <CounterTag
                count={eventContext.state.event.calendar.limits.capacity}
                size="sm"
                border={true}
              />
            </Box>
            <Box
              className="box-default"
              sx={{ mt: configEntzy.APP_SPACING_NSM }}
            >
              <Typography variant="h1">
                <span className="fa-layers fa-fw">
                  <FontAwesomeIcon
                    icon={iconLine}
                    transform="rotate-90 down-6 grow-24"
                  />
                  <FontAwesomeIcon
                    icon={iconRectangle}
                    transform="up-1"
                    color="#333"
                  />
                  <FontAwesomeIcon
                    icon={iconPeopleMax}
                    transform="shrink-7 up-1"
                    color="orange"
                  />
                </span>
              </Typography>
            </Box>
            <Box className="box-default relative pop-up">
              <Box
                className="box-inline bg-white"
                sx={{
                  borderRadius: configEntzy.BORDER_SIZE_MD,
                  pl: configEntzy.APP_SPACING_SM,
                  pr: configEntzy.APP_SPACING_SM,
                }}
              >
                <Typography variant="subtitle2" color="black">
                  Capacity
                </Typography>
              </Box>
            </Box>
            <Box className="box-default">
              <Typography variant="body1">&nbsp;</Typography>
            </Box>
          </Box>
        </Box>

        {/* ------------- CHARTS FOR DATE STATUS ------------- */}
        <Box
          className="box-default"
          sx={{
            mt: configEntzy.APP_SPACING_NXS3X,
          }}
        >
          <Box
            className="box-default nooverflow"
            sx={{
              borderRadius: configEntzy.BORDER_SIZE_XL,
            }}
          >
            <DateNavBar />

            <Box
              className="box-default half-width"
              sx={{
                pointerEvents: "none",
              }}
            >
              <BarChart
                // xAxis={[
                //   {
                //     id: "topAxis",
                //     label: "Launch Target",
                //   },
                //   {
                //     id: "bottomAxis",
                //     label: "Launch Target",
                //   },
                // ]}
                // borderRadius={10}
                // topAxis="topAxis"
                // leftAxis={null}
                // rightAxis={null}
                yAxis={[
                  {
                    id: "leftAxis",
                    scaleType: "band",
                    dataKey: "displayDate",
                    colorMap: {
                      type: "ordinal",
                      values: barNames(tickingDataset),
                      colors: barColors(barValues(tickingDataset)),
                    },
                  },
                  {
                    id: "rightAxis",
                    scaleType: "band",
                    dataKey: "displayDate",
                  },
                ]}
                series={[
                  {
                    dataKey: "progressTotal",
                    color: "#ff9900",
                    stack: "main",
                  },
                  {
                    dataKey: "targetTotal",
                    color: "#333333",
                    stack: "main",
                  },
                ]}
                barLabel={(item, context) => {
                  let itemData = tickingDataset[item.dataIndex];
                  let displayLabel = itemData.isLaunched
                    ? `${itemData.displayShort} ${configEntzy.URL_POINTERS.BOT_STYLED_LAUNCHED}`
                    : !itemData.isExpired && itemData.progressTotal > 0
                      ? itemData.displayShort +
                        " @ " +
                        itemData.progressTotal +
                        "%"
                      : itemData.displayDate;
                  // set label to appear left or right of bar (toggle 50 case with -0 and -1)
                  let displayReturn = item.value < 50 ? null : displayLabel;
                  if (item.value === 50 && item.seriesId.endsWith("-1")) {
                    displayReturn = null;
                  }
                  return displayReturn;
                }}
                dataset={tickingDataset}
                layout="horizontal"
                height={dateRange * barHeight}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                grid={{
                  horizontal: true,
                  vertical: true,
                }}
                tooltip={{ trigger: "none" }}
                skipAnimation={true}
                sx={{
                  ".MuiBarLabel-root": {
                    fill: "white",
                  },
                  ".MuiChartsAxis-tickLabel": {
                    fill: "white",
                  },
                  "& .MuiChartsAxis-line": {
                    display: "none",
                  },
                  "& *": {
                    pointerEvents: "auto",
                  },
                }}
                onItemClick={(event, identifier) => {
                  const index = identifier.dataIndex;
                  handleDateSelect(tickingDataset[index].date);
                }}
              />
            </Box>
            <Box
              className="box-default half-width"
              sx={{
                pointerEvents: "none",
              }}
            >
              <BarChart
                yAxis={[
                  {
                    id: "leftAxis",
                    scaleType: "band",
                    dataKey: "displayDate",
                    colorMap: {
                      type: "ordinal",
                      values: barNames(launchedDataset),
                      colors: barColors(barValues(launchedDataset)),
                    },
                  },
                  {
                    id: "rightAxis",
                    scaleType: "band",
                    dataKey: "displayDate",
                  },
                ]}
                series={[
                  {
                    dataKey: "progressTotal",
                    color: "#ff9900",
                    stack: "main",
                  },
                  {
                    dataKey: "targetTotal",
                    color: "#333333",
                    stack: "main",
                  },
                ]}
                barLabel={(item, context) => {
                  let itemData = launchedDataset[item.dataIndex];
                  let displayLabel = itemData.isCancelled
                    ? "Cancelled"
                    : itemData.isLaunched && itemData.isInPast
                      ? "Completed"
                      : itemData.isExpired && itemData.isInPast
                        ? "Expired"
                        : itemData.isInNoticePeriod && !itemData.isLaunched
                          ? "Past Notice Period"
                          : !itemData.isLaunched &&
                              itemData.date.isRunnerLaunchable
                            ? "Launchable!"
                            : itemData.isLaunched && itemData.progressTotal > 0
                              ? `LIVE ` +
                                (itemData.date.progress.remaining.capacity > 0
                                  ? itemData.date.progress.remaining.capacity +
                                    " Left"
                                  : "Maxed Out") +
                                (eventContext.state.event.data.LaunchPerTicket
                                  ? ` (${itemData.leadTicket.shortName})`
                                  : ``)
                              : itemData.tickingTotal > 0
                                ? `${itemData.tickingCount} Offer${
                                    itemData.tickingCount !== 1 ? "s" : ""
                                  }` +
                                  (itemData.date.isRunnerOffer ? " 🟢" : "") +
                                  (eventContext.state.event.data.LaunchPerTicket
                                    ? ` (${itemData.leadTicket.shortName})`
                                    : ``)
                                : !itemData.isLaunched &&
                                    itemData.date.isRunnerOffer
                                  ? "Run Team Offered!"
                                  : itemData.isLaunched &&
                                      itemData.tickingTotal === 0
                                    ? "Run Team Confirmed!"
                                    : "Open for Offers";
                  // set label to appear left or right of bar (toggle 50 case with -0 and -1)
                  let displayReturn = item.value < 50 ? null : displayLabel;
                  if (item.value === 50 && item.seriesId.endsWith("-1")) {
                    displayReturn = null;
                  }
                  return displayReturn;
                }}
                dataset={launchedDataset}
                layout="horizontal"
                height={dateRange * barHeight}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                grid={{
                  horizontal: true,
                  vertical: true,
                }}
                tooltip={{ trigger: "none" }}
                skipAnimation={true}
                sx={{
                  ".MuiBarLabel-root": {
                    fill: "white",
                  },
                  ".MuiChartsAxis-tickLabel": {
                    fill: "white",
                  },
                  "& .MuiChartsAxis-line": {
                    display: "none",
                  },
                  "& *": {
                    pointerEvents: "auto",
                  },
                }}
                onItemClick={(event, identifier) => {
                  const index = identifier.dataIndex;
                  handleDateSelect(launchedDataset[index].date);
                }}
              />
            </Box>

            <DateNavBar />
          </Box>
        </Box>

        <RoomDatesChartItemDetail
          {...props}
          date={mainContext.state.viewer.trail.eventDateSelect}
          expandDateDetail={
            mainContext.state.viewer.trail.eventDateSelect
              ? mainContext.state.viewer.trail.eventDateSelect.id
              : null
          }
          handleExpandDateDetail={() => handleDateSelect(null)}
        />
      </Box>
    </Box>
  );
}

export default RoomLaunchpadUpdates;
